import React, { useMemo } from 'react';
import cx from 'classnames';
import css from './Vulnerabilities.module.scss';
import { VulnerabilityItem } from 'modules/cm/Vulnerabilities/store/store';
import { CaseDetails } from '../../../CaseDetailsPage.types';
import VulnerabilityForm from './VulnerabilityForm';
import { FormProvider, useForm } from 'react-hook-form';
import { VulnerabilityId } from 'modules/cm/Vulnerabilities/Vulnerability';
import useYupValidationResolver from 'form/yup/reactHookFormResolver';
import validationSchema from './validationSchema';
import { Button } from 'modules/af_cm_ui_kit';
import { FormVulnerbaility } from './Vulnerability';

interface Props {
  item: VulnerabilityItem;
  categoryResources: CaseDetails['resources']['vulnerabilityCategories'];
  informationResources: CaseDetails['resources']['informationProvidedBy'];
  onDelete: () => void;
  onSave: (id: VulnerabilityId, vulnerability: FormVulnerbaility) => Promise<void>;
}

const ArrowIcon = () => {
  return (
    <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.41 8.58L12 13.17l4.59-4.59L18 10l-6 6-6-6 1.41-1.42z" />
    </svg>
  );
};

const VulnerabilityListItem = ({ item, categoryResources, informationResources, onSave, onDelete }: Props) => {
  const foundResource = categoryResources.find((resource) => resource.value === item.vulnerability.category);
  const category = foundResource ? foundResource.option : '';

  const methods = useForm<FormVulnerbaility>({
    mode: 'onBlur',
    resolver: useYupValidationResolver(validationSchema()),
    defaultValues: item.vulnerability,
  });

  const { handleSubmit, reset } = methods;

  const summaryRef = useMemo(() => React.createRef<HTMLDetailsElement>(), []);

  const onSubmit = handleSubmit(async (vulnerability: FormVulnerbaility) => {
    try {
      await onSave(item.vulnerability.id, vulnerability);
      handleToggleClick();
    } catch (e) {}
  });

  const handleSummaryClick = (e: React.MouseEvent<HTMLDetailsElement>) => {
    if (e.currentTarget === summaryRef.current) {
      e.preventDefault();
    }
  };

  const handleToggleClick = () => {
    if (summaryRef && summaryRef.current) {
      summaryRef.current.dispatchEvent(new MouseEvent('click'));
    }
  };

  const handleCancelClick = () => {
    reset(item.vulnerability);
    handleToggleClick();
  };

  return (
    <details className={css.details}>
      <summary className={css.summary} onClick={handleSummaryClick} ref={summaryRef}>
        <div className={css.description}>
          <p className={css.category}>{category}</p>
          <p className={css.date}>Date recorded: {item.vulnerability.dateRecorded}</p>
        </div>
        <div className={css.expand} onClick={handleToggleClick}>
          View/Edit
          <ArrowIcon />
        </div>
        <div className={css.collapse} onClick={handleCancelClick}>
          Close/Cancel
          <ArrowIcon />
        </div>
      </summary>

      <div className={css.content}>
        <FormProvider<FormVulnerbaility> {...methods}>
          <form onSubmit={onSubmit}>
            <>
              <VulnerabilityForm categoryResources={categoryResources} informationResources={informationResources} />

              <div className={cx(css.stack, css.formActions)}>
                <Button mode="ghost" type="button" onClick={handleCancelClick}>
                  Cancel
                </Button>
                <Button type="submit" mode="primary">
                  Update
                </Button>
              </div>

              <button type="button" onClick={onDelete} className={cx(css.stack, css.deleteVulnerability)}>
                Delete
              </button>
            </>
          </form>
        </FormProvider>
      </div>
    </details>
  );
};

export default VulnerabilityListItem;
